import React, { useContext, useEffect, useState, useRef, useMemo } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import { Scrollbars } from 'react-custom-scrollbars';
import { ThemeContext } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  getIOTDocument,
  onUpdateIOTDocument,
} from '../../../../graphql/iot-documents';
import {
  getAllVariables,
  addVariableToUser,
  removeVariableToUser,
  getVariableGroups,
} from '../../../../services';
import {
  GaugeChart,
  Button,
  Input,
  Loader,
  Sparkline,
} from '../../../../components';
import { icons, themes } from '../../../../assets';
import {
  GetColor,
  notifyError,
  notifyWarning,
  historicKey,
} from '../../../../utils';
import {
  getDefaultVariablesFor,
  FAVORITES_MIN_TO_HIDE_DEFAULTS,
} from '../../../../constants';
import { isBlueboxThings } from '../../../../constants/bluebox';
import { buildSimulatedThingData } from '../../../../utils/simulator';
import { useSimulator } from '../../../../contexts/simulator';
import * as s from './styled-velocimetro';

// Quantos pontos manter no buffer rolling de cada gauge — alimenta o
// sparkline inline. 30 pontos cobrem aprox. ultimos minutos com taxa
// de subscription tipica do IoT (~10s).
const GAUGE_SPARK_BUFFER = 30;

let useClickOutside = (handler) => {
  const floatRef = useRef();
  // useRef pra manter referencia estavel do handler sem disparar re-run
  // do effect (que registrava/desregistrava o listener a cada render,
  // acumulando handlers fantasmas).
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    let maybeHandler = (event) => {
      if (floatRef.current && !floatRef.current.contains(event.target)) {
        handlerRef.current();
      }
    };

    document.addEventListener('mousedown', maybeHandler);

    return () => {
      document.removeEventListener('mousedown', maybeHandler);
    };
  }, []);

  return floatRef;
};

export default ({
  things,
  selectedPeriod,
  selectedVar,
  setSelectedVar,
  aplicacao,
  thing,
  setLoadingGraph,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  // Seletores especificos: antes o terceiro selecionava state INTEIRO,
  // re-renderizando o componente a cada dispatch global. Agora cada
  // seletor pega so o campo necessario.
  const favorites = useSelector((state) => state.usuario.favorites);
  const rawThingsData = useSelector((state) => state.aplicacao.velocimetro);
  // Modo simulador (so dev): sem dado realtime da thing, preenche o gauge
  // com valores fake pra visualizar o layout. Dado real sempre vence.
  const { enabled: simulatorEnabled } = useSimulator();
  const thingsData = useMemo(() => {
    if (!simulatorEnabled) return rawThingsData;
    const existing = (rawThingsData && rawThingsData[thing.id]) || null;
    const hasReal = existing && Object.keys(existing).length > 0;
    if (hasReal) return rawThingsData;
    return {
      ...(rawThingsData || {}),
      [thing.id]: { ...buildSimulatedThingData(0), ts: Date.now() },
    };
  }, [rawThingsData, simulatorEnabled, thing.id]);
  // Cache de variables da thing atual (set por getAllVariables). Lookup
  // por thingName (key do iot slice). Selector estreito pra nao re-
  // render a cada tick de subscription (que atualiza state.iot inteiro).
  const cachedVariables = useSelector((state) =>
    thing && thing.thing && state.iot && state.iot[thing.thing]
      ? state.iot[thing.thing].variables
      : null
  );
  const historic = useSelector((state) => state.historic);
  const [changeVariable, setChangeVariable] = useState(null);
  const [search, setSearch] = useState('');
  const [allVariables, setAllVariables] = useState();
  const [filteredVariables, setFilteredVariables] = useState([]);
  const [loadingVariable, setLoadingVariable] = useState(false);
  const [favoriteThingVars, setFavoriteThingsVars] = useState([]);
  const [openChangeVars, setOpenChangeVars] = useState(false);
  // Buffer rolling por variavel — alimenta o sparkline inline em cada
  // gauge. Estrutura: { [ihmname]: [{ value, ts }, ...] }. Mantido em
  // memoria, max GAUGE_SPARK_BUFFER pontos por variavel. Reset ao
  // trocar thing.
  const [variableSeries, setVariableSeries] = useState({});
  const isBluebox = isBlueboxThings(things);

  // eslint-disable-next-line prefer-const
  let floatRef = useClickOutside(() => {
    setChangeVariable(null);
  });

  // Variaveis exibidas nos gauges. Logica de fallback inteligente:
  //   1) Se o operador favoritou pelo menos FAVORITES_MIN_TO_HIDE_DEFAULTS
  //      variaveis, mostramos so os favoritos dele (respeitamos escolha).
  //   2) Se nao favoritou nada (ou menos que o minimo), mostramos um
  //      conjunto padrao por tipo de maquina (UCHA / EBA) — assim a tela
  //      e util sem configuracao previa.
  // Cada variavel exibida ganha a flag isDefault pra UI sinalizar visualmente.
  //
  // DECLARADO ANTES dos useEffects que o referenciam — TDZ se inverter ordem.
  const displayVariables = useMemo(() => {
    if (!allVariables || !allVariables.length) return [];
    const favoriteIds = new Set(favoriteThingVars.map(({ id }) => String(id)));
    const favoriteMatches = allVariables.filter(({ id }) =>
      favoriteIds.has(String(id))
    );

    // Operador tem favoritos suficientes — respeita escolha dele.
    if (favoriteMatches.length >= FAVORITES_MIN_TO_HIDE_DEFAULTS) {
      return favoriteMatches.map((v) => ({ ...v, isDefault: false }));
    }

    // Sem favoritos: usa defaults do tipo da maquina (ou da aplicacao).
    const machineType =
      (thing && thing.type && thing.type.type) ||
      (aplicacao && aplicacao.type && aplicacao.type.type) ||
      'ucha';
    const defaultIhmNames = getDefaultVariablesFor(machineType);
    // Mantem a ordem dos defaults (importante — primeira variavel default
    // vira a selecionada pelo grafico). Filtra silenciosamente as que nao
    // existem no allVariables retornado pelo backend.
    const ihmIndex = new Map(allVariables.map((v) => [v.ihmname, v]));
    return defaultIhmNames
      .map((name) => ihmIndex.get(name))
      .filter(Boolean)
      .map((v) => ({ ...v, isDefault: true }));
  }, [allVariables, favoriteThingVars, thing, aplicacao]);

  useEffect(() => {
    if (allVariables && allVariables.length) {
      let newFilteredVariables = allVariables.filter(({ name }) =>
        name.toLowerCase().includes(search)
      );
      const favoriteNames = new Set(
        favoriteThingVars.map(({ id }) => String(id))
      );
      newFilteredVariables = newFilteredVariables.filter(({ id }) => {
        return !favoriteNames.has(String(id));
      });
      setFilteredVariables(newFilteredVariables);
    }
  }, [search, allVariables, favoriteThingVars]);

  useEffect(() => {
    // Auto-seleciona a PRIMEIRA variavel da lista exibida (favorito ou
    // default). Antes so funcionava com favoritos — chart ficava vazio
    // pra operadores sem nada favoritado.
    if (!selectedVar.id && displayVariables.length) {
      setSelectedVar(displayVariables[0]);
    } else if (displayVariables.length) {
      const thingToFecth = thing[0] ? thing[0].thing : thing.thing;
      const thindIdToFecth = thing[0] ? thing[0].id : thing.id;
      // Cache chaveado por thingId+variavel (historicKey) — sem isso o
      // guard achava cache de OUTRA maquina (mesmo ihmname) e nao
      // refetchava ao trocar de maquina.
      const cacheKey = historicKey(thindIdToFecth, selectedVar.ihmname);
      const cached = historic[cacheKey] && historic[cacheKey][selectedPeriod];
      // Array vazio pode ter sido gravado por uma falha temporaria da API.
      // Ele nao deve bloquear novas tentativas ao trocar periodo/variavel.
      if (Array.isArray(cached) && cached.length > 0) return;
      setLoadingGraph(true);
      dispatch({
        type: 'HISTORIC/FETCH_VARIABLE_HISTORIC',
        period: selectedPeriod,
        thing: thingToFecth,
        variable: selectedVar.ihmname,
        thingId: thindIdToFecth,
        variablesId: selectedVar.id,
      });
    }
    // `historic` é lido como guard para evitar refetch; adicioná-lo causaria loop
    // (dispatch atualiza historic). setSelectedVar/setLoadingGraph vêm do parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVar, selectedPeriod, displayVariables, thing, dispatch]);

  useEffect(() => {
    if (isBluebox) return undefined;
    let mounted = true;
    const subscriptions = [];

    const loadThings = async () => {
      const [iotQuery, updateIotSubscription] = await Promise.all([
        getIOTDocument(),
        onUpdateIOTDocument(),
      ]);
      if (!mounted) return;

      const refreshThing = async ({ id, thing }) => {
        try {
          const response = await API.graphql(
            graphqlOperation(iotQuery, { thingName: thing })
          );
          const iot =
            response.getIOT || (response.data && response.data.getIOT);

          if (!iot || !mounted) return false;
          dispatch({
            type: 'APLICACAO/SET_VELOCIMETRO_DATA',
            data: { [id]: iot },
          });
          return true;
        } catch (_) {
          // A subscription continua ativa e o proximo ciclo tenta novamente.
          return false;
        }
      };

      await Promise.all(
        things.map(async ({ id, thing }) => {
          const loaded = await refreshThing({ id, thing });

          if (loaded) {
            if (!mounted) return;
            let subscription;
            subscription = API.graphql(
              graphqlOperation(updateIotSubscription, { thingName: thing })
            ).subscribe({
              next: (subscribeData) => {
                if (
                  subscribeData.value &&
                  subscribeData.value.data &&
                  subscribeData.value.data.onUpdateIOT
                ) {
                  if (!mounted) return;
                  dispatch({
                    type: 'APLICACAO/SET_VELOCIMETRO_DATA',
                    data: { [id]: subscribeData.value.data.onUpdateIOT },
                  });
                }
              },
              error: () => {
                subscription?.unsubscribe();
              },
            });

            subscriptions.push(subscription);
          } else {
            notifyWarning('Nao foi possivel buscar as informacoes da Thing');
          }
        })
      );
    };

    loadThings();

    return () => {
      mounted = false;
      subscriptions.forEach((sub) => sub?.unsubscribe && sub.unsubscribe());
    };
    // `thingsData` é lido para spread mas atualizar via dispatch causaria loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [things, dispatch, isBluebox]);

  useEffect(() => {
    if (!thing.id) return;
    // Cache hit: variaveis ja foram buscadas nesta sessao pra essa
    // thing. Pula as DUAS HTTP requests (getVariableGroups +
    // getAllVariables) e popula allVariables direto do Redux iot.
    // Ganho perceptivel: ao voltar pra mesma maquina, o chart pula
    // direto pro fetch de historic (~300-700ms a menos no carregamento).
    if (cachedVariables && cachedVariables.length) {
      setAllVariables(cachedVariables);
      return;
    }
    setLoadingVariable(true);
    getVariableGroups(thing.id)
      .then(({ data }) => {
        const supervisorioId = data
          .filter(({ group }) => group === 'supervisorio')
          .pop().id;
        // Bluebox possui mais de 50 variaveis; carregar apenas a primeira
        // pagina fazia favoritos validos desaparecerem dos gauges.
        getAllVariables(thing.id, 1, isBluebox ? 200 : 50, supervisorioId)
          .then((resp) => {
            setAllVariables(resp.data);
            dispatch({
              type: 'IOT/SET_VARIABLES_THING',
              variables: resp.data,
              thingName: thing.thing,
            });
          })
          .catch(() => {
            notifyWarning('Erro ao buscar as variáveis');
          });
      })
      .catch(() => {})
      .finally(() => setTimeout(() => setLoadingVariable(false), 200));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thing, dispatch, isBluebox]);

  useEffect(() => {
    let newVars = [];

    favorites?.vars?.forEach((thingFav) => {
      if (String(thingFav.id) === String(thing.id)) {
        newVars.push(thingFav.variables);
      }
    });
    newVars[0] ? setFavoriteThingsVars(newVars[0]) : setFavoriteThingsVars([]);
  }, [thing, favorites]);

  // Buffer rolling por variavel exibida — alimenta o sparkline inline em
  // cada gauge. Push novo ponto quando o ts da thing muda (nova dispatch
  // de subscription). lastTsRef evita re-push em renders sem dado novo.
  const lastSeenTsRef = useRef(0);
  useEffect(() => {
    const currentData = thingsData[thing.id];
    if (!currentData) return;
    const ts = currentData.ts || Date.now();
    if (ts === lastSeenTsRef.current) return;
    lastSeenTsRef.current = ts;

    setVariableSeries((prev) => {
      const next = { ...prev };
      displayVariables.forEach((variable) => {
        const raw = currentData[variable.ihmname];
        const value = typeof raw === 'number' ? raw : parseFloat(raw);
        if (!Number.isFinite(value)) return;
        const series = prev[variable.ihmname] || [];
        const updated = [...series, { value, ts }];
        next[variable.ihmname] =
          updated.length > GAUGE_SPARK_BUFFER
            ? updated.slice(updated.length - GAUGE_SPARK_BUFFER)
            : updated;
      });
      return next;
    });
  }, [thingsData, thing.id, displayVariables]);

  // Reset buffer ao trocar de thing — evita misturar series de maquinas
  // diferentes ao navegar entre elas.
  useEffect(() => {
    setVariableSeries({});
    lastSeenTsRef.current = 0;
  }, [thing.id]);

  const { title, colors } = useContext(ThemeContext);

  const info = [
    {
      label: 'Normal',
      color: themes.statusN,
    },
    {
      label: 'Irregular',
      color: themes.statusW,
    },
    {
      label: 'Atenção',
      color: themes.statusE,
    },
  ];

  const renderTrack = ({ style }) => {
    const finalStyle = {
      ...style,
      width: 10,
      borderRadius: 16,
      background: `${colors.lightergrey}`,
      right: 2,
      bottom: 2,
      top: 2,
    };
    return <div style={finalStyle} />;
  };

  const handleChangeVariable = (actualVar, newVar) => {
    setChangeVariable(null);
    setSearch('');
    setLoadingVariable(true);
    const newFavorites = favoriteThingVars.filter(
      ({ id }) => id !== actualVar.id
    );
    const newVars = favorites.vars.filter(({ id }) => id !== thing.id);
    newFavorites.push(newVar);
    newVars.push({ ...thing, variables: newFavorites });

    removeVariableToUser(actualVar.id, thing.id)
      .then(() => {
        addVariableToUser(newVar.id, thing.id)
          .then(() => {
            dispatch({
              type: 'USUARIO/SET_FAVORITE_VARS',
              vars: newVars,
            });
          })
          .catch(() =>
            notifyError('Houve um erro ao atualizar a variável favorita')
          )
          .finally(() => setLoadingVariable(false));
      })
      .catch(() => {
        setLoadingVariable(false);
        notifyError('Houve um erro ao atualizar a variável favorita');
      });
  };

  const renderOptions = (variable) => (
    <s.VariablesChangeContainer
      ref={floatRef}
      top={document.getElementById(variable.id).offsetTop + 150}
      left={document.getElementById(variable.id).offsetLeft}
      window={window.innerWidth}
    >
      <s.Label>Escolha a variável que substituirá:</s.Label>
      <s.Label color={colors.red} style={{ margin: '10px 0' }}>
        {variable.name}
      </s.Label>
      <Input
        maxLength={30}
        size="15px"
        noPadding
        src={icons.SearchIcon}
        placeholder="Pesquise uma variável"
        width="280px"
        style={{ margin: 'auto' }}
        height="35px"
        value={search}
        onChange={(e) => setSearch(e.target.value.toLowerCase())}
      />
      <Scrollbars
        renderTrackVertical={renderTrack}
        renderThumbVertical={() => <s.ThumbVertical />}
        style={{ height: '250px', width: '100% !important' }}
      >
        {filteredVariables.length > 0 ? (
          filteredVariables.map((option) => (
            <s.Option
              key={option.name}
              id={option.name}
              onClick={() => handleChangeVariable(selectedVar, option)}
            >
              <label>{option.name}</label>
            </s.Option>
          ))
        ) : (
          <s.Option>
            <label>Variável não encontrada</label>
          </s.Option>
        )}
      </Scrollbars>
    </s.VariablesChangeContainer>
  );
  // Detecta se estamos exibindo defaults (nenhum favorito do operador
  // ainda) — banner aparece convidando a personalizar.
  const isShowingDefaults =
    displayVariables.length > 0 && displayVariables.every((v) => v.isDefault);
  const navigateToFavorites = () => {
    if (!thing || !thing.id) {
      notifyWarning('Aguarde o carregamento da máquina');
      return;
    }
    history.push({
      pathname: '/dashboard/customer/plant/appliance',
      state: { thing, aplicacao },
    });
  };

  return (
    <s.Container>
      <s.HeaderContainer>
        <div id="media">
          <img src={icons.Star} alt="" />
          <s.Label color={colors.orange}>Favoritos</s.Label>
        </div>
        <s.LegendInline>
          {info.map(({ label, color }) => (
            <s.LegendItem key={label}>
              <s.Circle background={color} />
              <s.Label color={colors.label}>{label}</s.Label>
            </s.LegendItem>
          ))}
        </s.LegendInline>
        <Button
          hover
          label="Ver todos"
          width="100px"
          height="30px"
          color={colors.blue}
          onClick={navigateToFavorites}
          background={title === 'light' ? colors.white : colors.darker}
        />
      </s.HeaderContainer>
      {isShowingDefaults && (
        <s.DefaultsBanner>
          <span>
            Exibindo <strong>variáveis padrão</strong> para este tipo de
            máquina. Personalize favoritando outras variáveis.
          </span>
          <s.DefaultsBannerCTA type="button" onClick={navigateToFavorites}>
            Personalizar
          </s.DefaultsBannerCTA>
        </s.DefaultsBanner>
      )}
      <s.ContentContainer isLoading={loadingVariable}>
        {loadingVariable ? (
          <Loader />
        ) : (
          Object.entries(thingsData).length > 0 &&
          displayVariables.map((variable, index) => {
            return (
              <s.GaugeContainer
                id={variable.id}
                key={variable.id}
                selected={selectedVar.id === variable.id}
              >
                {variable.isDefault && (
                  <s.DefaultTag title="Variável padrão — favorite no Ver Todos para personalizar">
                    Padrão
                  </s.DefaultTag>
                )}
                <>
                  {variable.range[0].category !== 'NA' ? (
                    <GaugeChart
                      arcsLength={variable.proportion}
                      ranges={variable.range}
                      color={GetColor(variable.status, 'ranges')}
                      GaugeWidth={window.innerWidth > 1585 ? '130px' : '80px'}
                      onClick={() => {
                        setChangeVariable(null);
                        setSelectedVar(variable);
                      }}
                      graphLabel={variable.name}
                      unit={variable.range[0].unit}
                      value={
                        thingsData[thing.id] &&
                        thingsData[thing.id][variable.ihmname]
                      }
                      index={index}
                      labelFontSize="14px"
                      needleColorDefault={colors.red}
                    />
                  ) : (
                    <s.NoGraphContainer
                      onClick={() => {
                        setChangeVariable(null);
                        setSelectedVar(variable);
                      }}
                    >
                      <s.Label color={colors.label}>{variable.name}</s.Label>
                      <s.Label
                        style={{ marginBottom: 10, marginTop: 10 }}
                        color={colors.darkerblue}
                      >
                        {thingsData[thing.id] &&
                          thingsData[thing.id][variable.ihmname]}{' '}
                        {variable.range[0].unit}
                      </s.Label>
                    </s.NoGraphContainer>
                  )}
                  {/* Sparkline inline: tendencia das ultimas amostras
                      (buffer rolling alimentado pela subscription). So
                      renderiza quando ha pelo menos 2 pontos — abaixo
                      disso o componente retorna null automaticamente. */}
                  <s.SparklineWrapper>
                    <Sparkline
                      data={variableSeries[variable.ihmname] || []}
                      width={110}
                      height={20}
                      color={
                        GetColor(variable.status, 'ranges') || colors.lightblue
                      }
                      fill
                    />
                  </s.SparklineWrapper>
                  <s.ChangeVariable
                    name="trocar"
                    title="Trocar variável"
                    aria-label="Trocar variável"
                    onClick={(e) => {
                      e.stopPropagation();

                      if (changeVariable === index || openChangeVars) {
                        setChangeVariable(null);
                      } else {
                        setOpenChangeVars(false);
                        setSearch('');
                        setChangeVariable(index);
                        setSelectedVar(variable);
                      }
                    }}
                  >
                    <s.ArrowDown />
                  </s.ChangeVariable>
                  {index === changeVariable && renderOptions(variable, index)}
                  {/* {index === changeVariable && renderOptions(variable, index)} */}
                </>
              </s.GaugeContainer>
            );
          })
        )}
        {/* UM unico slot "+ Adicionar" quando ha espaco — affordance de
            descoberta sem virar parede de placeholders. Antes preenchia ate
            4 slots vazios, que dominavam a faixa quando o operador nao tinha
            favoritado (caso comum). Clique navega pra tela de favoritar. */}
        {!loadingVariable &&
          Object.entries(thingsData).length > 0 &&
          displayVariables.length > 0 &&
          displayVariables.length < 8 && (
            <s.EmptySlot
              type="button"
              onClick={navigateToFavorites}
              title="Adicionar variável aos favoritos"
            >
              <s.EmptySlotPlus>+</s.EmptySlotPlus>
              <s.EmptySlotLabel>Adicionar</s.EmptySlotLabel>
            </s.EmptySlot>
          )}
      </s.ContentContainer>
    </s.Container>
  );
};
